import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DownArrow from "../../assets/svgs/down-arrow.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";
import ActionsMenu from "../../components/ActionsMenu";
import ColumnCustomizer from "../../components/ColumnCustomizer";
import ContractModal from "../../components/ConfirmationModals/ContractModal";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import KpiSlider from "../../components/KpiSlider";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import LeadDataContainer from "../../pages/Leads/components/LeadDataContainer";
import { BASE_URL } from "../../utils/Element";
import { initialExecutedFilterData } from "../../utils/initialData";
import FellOutContract from "../UnderContract/components/FellOutContract";
import EditExecutedModal from "./component/EditExecutedModal";
import ExecutedAddModal from "./component/ExecutedAddModal";
import ExecutedFilter from "./component/ExecutedFilter";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const ExecutedLOI = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);

  // ==================== States ====================
  const [loading, setLoading] = useState(true);
  const [leadsData, setLeadsData] = useState([]);
  const [dashboardData, setDashboardData] = useState(null); // For stats

  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [expandedRows, setExpandedRows] = useState({});

  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  });

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showfelloutcontract, setShowFellOutContract] = useState(false);
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialExecutedFilterData;
  });
  const allColumns = [
    { key: 'details', label: 'Details', fixed: true },
    { key: 'executed_loi_date', label: 'Executed LOI Date', fixed: true },
    { key: 'property_name', label: 'Property Name' },
    { key: 'acquisition', label: 'Acquisition' },
    { key: 'disposition', label: 'Disposition' },
    { key: 'lease_type', label: 'Lease Type' },
    { key: 'lease_term_remaining', label: 'Lease Term Remaining' },
    { key: 'noi', label: 'NOI' },
    { key: 'contract_price', label: 'Contract Price' },
    { key: 'contract_cap_rate', label: 'Contract Cap Rate' },
    { key: 'asking_price_percent', label: '% of Asking Price' },
    { key: 'gci_company', label: 'GCI to Company' },
    { key: 'actions', label: 'Actions', fixed: true },
  ];
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('executedLoiVisibleColumns');
    return saved
      ? JSON.parse(saved)
      : allColumns.map(col => col.key); // Show all by default
  });

  const queryParams = new URLSearchParams(location.search);
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');
  const [filterStatus, setFilterStatus] = useState({
    avg_commission_per_transaction_ytd: false,
    executedlo_buyer: false,
    executedlo_seller: false,
    executedloi_gcl_to_company_ytd: false,
    executedloi_volume_ytd: false,
    executedloi_ytd: false,
    new_executedloi_quarter: false,
    jan: false, feb: false, mar: false, apr: false, may: false,
    jun: false, jul: false, aug: false, sep: false, oct: false,
    nov: false, dec: false,
  });

  // View Toggle: My vs Team
  const viewOptions = [
    { value: 1, label: "My Executed LOI" },
    { value: 3, label: "Team Executed LOI" },
  ];
  const [activeView, setActiveView] = useState(3); // Default: Team


  // ==================== Effects ====================
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    search.trim() ? params.set('search', search) : params.delete('search');
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate]);

  // Fetch Data
  useEffect(() => {
    fetchExecuted();
    fetchDashboardStats();
  }, [activeView, currentPage, itemPerPage, search, sortBy, sortDirection]);

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleUnderContract = () => {
    axios
      .delete(`${BASE_URL}/mark-as-under-contract/${selectedId}`, config)
      .then((res) => {
        setSelectedId("");
        onSuccess();
        setShowContractModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}&search=${search}`;

    const {
      created_at, property_name, noi, created_end_date, created_start_date,
      min_contract_price, max_contract_price, min_contract_cap_rate, max_contract_cap_rate,
      min_asking_price, max_asking_price, max_potential_gcl, broker
    } = filters;

    if (created_at) params += `&created_at=${encodeURIComponent(created_at)}`;
    if (created_start_date) params += `&created_start_date=${encodeURIComponent(created_start_date)}`;
    if (created_end_date) params += `&created_end_date=${encodeURIComponent(created_end_date)}`;
    if (property_name) params += `&property_name=${encodeURIComponent(property_name)}`;
    if (noi) params += `&noi=${encodeURIComponent(noi)}`;
    if (broker) params += `&broker=${encodeURIComponent(broker)}`;

    if (min_contract_price) params += `&min_contract_price=${encodeURIComponent(min_contract_price)}`;
    if (max_contract_price) params += `&max_contract_price=${encodeURIComponent(max_contract_price)}`;
    if (min_contract_cap_rate) params += `&min_contract_cap_rate=${encodeURIComponent(min_contract_cap_rate)}`;
    if (max_contract_cap_rate) params += `&max_contract_cap_rate=${encodeURIComponent(max_contract_cap_rate)}`;
    if (min_asking_price) params += `&min_asking_price=${encodeURIComponent(min_asking_price)}`;
    if (max_asking_price) params += `&max_asking_price=${encodeURIComponent(max_asking_price)}`;
    if (max_potential_gcl) params += `&max_potential_gcl=true`;

    // All filterStatus (Nothing removed)
    Object.entries(filterStatus).forEach(([key, value]) => {
      if (value) params += `&${key}=true`;
    });

    if (sortBy !== "id") {
      params += `&sort_by=${sortBy}&sort_direction=${sortDirection}`;
    }

    return params;
  };

  const fetchExecuted = (filters = filterData) => {
    setLoading(true);
    const queryParams = buildQueryParams(filters);

    axios
      .get(`${BASE_URL}/executeds?avtive_tab=${activeView}${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.executed || {};
        setLeadsData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchDashboardStats = () => {
    axios
      .get(`${BASE_URL}/dashboard-executedloi?active_tab=${activeView}`, config)
      .then((res) => {
        setDashboardData(res?.data?.executedloi_dashboard);
      })
      .catch((err) => console.error(err));
  };

  const onSuccess = () => {
    fetchExecuted();
    fetchDashboardStats();
  };

  const handleDelete = () => {
    setDisableDelete(true)
    axios
      .delete(`${BASE_URL}/delete-executed/${selectedId}`, config)
      .then((res) => {
        setSelectedId("")
        onSuccess()
        setShowDeleteModal(false)
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message)
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
      .finally(() => setDisableDelete(false))
  }

  // ==================== Handlers ====================
  const handleViewChange = (e) => {
    const newView = parseInt(e.target.value);
    setActiveView(newView);
    setCurrentPage(1);
    setExpandedRows({});

    // Reset filterStatus only when switching between Dashboard and List view
    if ((activeView <= 1 && newView >= 2) || (activeView >= 2 && newView <= 1)) {
      setFilterStatus({
        avg_commission_per_transaction_ytd: false,
        executedlo_buyer: false,
        executedlo_seller: false,
        executedloi_gcl_to_company_ytd: false,
        executedloi_volume_ytd: false,
        executedloi_ytd: false,
        new_executedloi_quarter: false,
        jan: false, feb: false, mar: false, apr: false, may: false,
        jun: false, jul: false, aug: false, sep: false, oct: false,
        nov: false, dec: false,
      });
    }
  };

  const handleBoxClick = (nameOrKey) => {
    const targetView = activeView === 0 ? 1 : 3;
    setActiveView(targetView);
    setCurrentPage(1);

    setFilterStatus({
      avg_commission_per_transaction_ytd: nameOrKey === "avg_commission_per_transaction_ytd",
      executedlo_buyer: nameOrKey === "executedlo_buyer",
      executedlo_seller: nameOrKey === "executedlo_seller",
      executedloi_gcl_to_company_ytd: nameOrKey === "executedloi_gcl_to_company_ytd",
      executedloi_volume_ytd: nameOrKey === "executedloi_volume_ytd",
      executedloi_ytd: nameOrKey === "executedloi_ytd",
      new_executedloi_quarter: nameOrKey === "new_executedloi_quarter",
      jan: nameOrKey === "jan",
      feb: nameOrKey === "feb",
      mar: nameOrKey === "mar",
      apr: nameOrKey === "apr",
      may: nameOrKey === "may",
      jun: nameOrKey === "jun",
      jul: nameOrKey === "jul",
      aug: nameOrKey === "aug",
      sep: nameOrKey === "sep",
      oct: nameOrKey === "oct",
      nov: nameOrKey === "nov",
      dec: nameOrKey === "dec",
    });
  };

  return (
    <BaseLayout>
      <div className="contact-header mb-3">
        <p className="head-1 dark-H">Executed LOI</p>

        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 justify-between items-center w-full md:w-auto flex-wrap">
            {/* My / Team Toggle */}
            <div className="relative min-w-[240px]">
              <select
                value={activeView}
                onChange={handleViewChange}
                className="w-full bg-white rounded-lg px-4 py-2.5 body-S dark-M cursor-pointer appearance-none"
              >
                {viewOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                <img src={DownArrow} alt="down" className="w-4 h-4" />
              </div>
            </div>

            <ExecutedFilter
              filterData={filterData}
              onSetFilterData={setFilterData}
              from="Executed LOI"
              onCallApiAgain={onSuccess}
            />

            <div className="search-box contacts relative flex-1 min-w-[200px]">
              <input
                type="text"
                className="body-S w-full"
                placeholder="Search..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ border: "1px solid #D8D8D8" }}
              />
            </div>

            {(activeView === 1 || activeView === 3) && (
              <button
                className="add-contact-button green-bg-H light-L body-S flex items-center gap-1"
                onClick={() => setShowColumnModal(true)}
              >
                Columns ⚙️
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ==================== STATISTICS CAROUSEL ==================== */}

      <div className="mb-8 w-full ">
        {dashboardData && (
          <KpiSlider
            data={[
              {
                text: "NEW EXECUTED LOI THIS QUARTER",
                value: dashboardData.new_executedloi_quarter,
                key: "new_executedloi_quarter",
              },
              {
                text: "TOTAL CURRENT EXECUTED LOI YTD",
                value: dashboardData.executedloi_ytd,
                key: "executedloi_ytd",
              },
              {
                text: "EXECUTED LOI TOTAL VOLUME YTD",
                value: `$${dashboardData.executedloi_volume_ytd?.toLocaleString() || 0}`,
                key: "executedloi_volume_ytd",
              },
              {
                text: "CURRENT POTENTIAL GCI TO COMPANY YTD",
                value: `$${dashboardData.executedloi_gcl_to_company_ytd?.toLocaleString() || 0}`,
                key: "executedloi_gcl_to_company_ytd",
              },
              {
                text: "AVG. COMMISSION PER TRANSACTION YTD",
                value: `$${dashboardData.avg_commission_per_transaction_ytd?.toLocaleString() || 0}`,
                key: "avg_commission_per_transaction_ytd",
              },
              {
                text: "EXECUTED LOI BUYER TRANSACTIONS YTD",
                value: dashboardData.executedlo_buyer,
                key: "executedlo_buyer",
              },
              {
                text: "EXECUTED LOI SELLER TRANSACTIONS YTD",
                value: dashboardData.executedlo_seller,
                key: "executedlo_seller",
              },
            ]}
            renderItem={(item) => (
              <div onClick={() => handleBoxClick(item.key)} className="cursor-pointer">
                <LeadDataContainer text={item.text} number={item.value} />
              </div>
            )}
          />

        )}
      </div>

      {/* ==================== TABLE ==================== */}
      <div className="light-bg-L pb-3 table-container">
        <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>

          {loading ? (
            <Loader />
          ) : leadsData?.length === 0 ? (
            <p className="body-N text-center mt-6 p-10">No Executed LOI Available</p>
          ) : (
            <>
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    {visibleColumns.includes("details") && <th>Details</th>}

                    {visibleColumns.includes("executed_loi_date") && (
                      <th>
                        <div className="table-header">
                          EXECUTED LOI DATE
                          <img
                            role="button"
                            src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("created_at");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="sort"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("property_name") && (
                      <th>
                        <div className="table-header">
                          PROPERTY NAME
                          <img
                            role="button"
                            src={sortBy !== "broker" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("broker");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="sort"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("acquisition") && (
                      <th>
                        <div className="table-header">
                          Acquisition
                          <img
                            role="button"
                            src={sortBy !== "acquisition" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("acquisition");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("disposition") && (
                      <th>
                        <div className="table-header">
                          Disposition
                          <img
                            role="button"
                            src={sortBy !== "disposition" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("disposition");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("lease_type") && (
                      <th>
                        <div className="table-header">
                          Lease Type
                          <img
                            role="button"
                            src={sortBy !== "lease_type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("lease_type");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("lease_term_remaining") && (
                      <th>
                        <div className="table-header">
                          Lease Term Remaining
                          <img
                            role="button"
                            src={sortBy !== "lease_remaining" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("lease_remaining");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("noi") && <th>NOI</th>}
                    {visibleColumns.includes("contract_price") && <th>CONTRACT PRICE</th>}
                    {visibleColumns.includes("contract_cap_rate") && <th>CONTRACT CAP RATE</th>}
                    {visibleColumns.includes("asking_price_percent") && <th>% OF ASKING PRICE</th>}
                    {visibleColumns.includes("gci_company") && <th>GCI TO COMPANY</th>}
                    {visibleColumns.includes("actions") && <th>Actions</th>}
                  </tr>
                </thead>

                <tbody>
                  {leadsData?.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr className="dark-H body-N">
                        {visibleColumns.includes("details") && (
                          <td>
                            <img
                              role="button"
                              src={expandedRows[el.id] ? UpArrow : DownArrow}
                              onClick={() => toggleRowDetails(el.id)}
                              alt=""
                            />
                          </td>
                        )}

                        {visibleColumns.includes("executed_loi_date") && (
                          <td>{moment(el?.created_at).tz('America/New_York').format("MM/DD/YY")}</td>
                        )}

                        {visibleColumns.includes("property_name") && (
                          <td className="head-5 capitalize">
                            <Link
                              to="#"
                              onClick={(e) => {
                                e.preventDefault();
                                if (el?.portfolio === 0) navigate(`/property/${el?.property_id}`);
                                else if (el?.link?.owner_info_type === "contact") navigate(`/contact/${el?.link?.owner?.id}`);
                                else if (el?.link?.owner_info_type === "company") navigate(`/company/${el?.link?.owner?.id}`);
                              }}
                            >
                              {el?.link?.property_name} <br />
                              {el?.link?.street_address} <br />
                              {el?.link?.state && el?.link?.city ? ` ${el?.link?.city}, ${el?.link?.state} ` : ""}
                            </Link>
                          </td>
                        )}

                        {visibleColumns.includes("acquisition") && <td>{el?.offer?.acquistion_name}</td>}
                        {visibleColumns.includes("disposition") && <td>{el?.offer?.disposition_name}</td>}

                        {visibleColumns.includes("lease_type") && (
                          <td className="text-center">{el?.lease_type}</td>
                        )}

                        {visibleColumns.includes("lease_term_remaining") && (
                          <td className="text-center">{el?.lease_term_remaining}</td>
                        )}

                        {visibleColumns.includes("noi") && <td>${el?.link?.noi_format}</td>}
                        {visibleColumns.includes("contract_price") && <td className="text-center">${el?.contract_price_format}</td>}
                        {visibleColumns.includes("contract_cap_rate") && (
                          <td className="text-center">{el?.contract_cap_rate_format}%</td>
                        )}
                        {visibleColumns.includes("asking_price_percent") && (
                          <td className="text-center">{el?.asking_price_format_percentage}%</td>
                        )}
                        {visibleColumns.includes("gci_company") && (
                          <td className="text-center">${el?.gross_commission?.gci_to_company}</td>
                        )}

                        {visibleColumns.includes("actions") && (
                          <td>
                            <div className="flex gap-3 justify-center">
                              <ActionsMenu
                                from="ExecutedLoi"
                                handleEdit={() => { setShowEditModal(true); setSelectedId(el?.id); }}
                                handleDelete={() => { setSelectedId(el?.id); setShowDeleteModal(true); }}
                                showOtherOption={true}
                                otherOptionTitle={"New Counter Offer"}
                                handleOtherOption={() => { setShowAddModal(true); setSelectedId(el?.id); }}
                                otherUnderContractTitle={<> Convert to <br /> Under Contract</>}
                                handleUnderContract={() => { setSelectedId(el?.id); setShowContractModal(true); }}
                                felloutcontractoption={<> Fell Out Of <br /> Executed LOI</>}
                                handlefelloutcontractOption={() => { setShowFellOutContract(true); setSelectedId(el?.id); }}
                              />
                            </div>
                          </td>
                        )}
                      </tr>

                      {/* Expanded Row */}
                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="">

                                {/* --- TOP INFO SECTION --- */}
                                <div className="grid grid-cols-5 gap-6">

                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.seller_broker?.length > 0
                                        ? el.offer.seller_broker.map((broker, index) => (
                                          <span key={broker.id || index}>
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.offer.seller_broker.length - 1 ? ", " : ""}
                                          </span>
                                        ))
                                        : ""}
                                    </p>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.seller_bds?.first_name} {el?.offer?.seller_bds?.last_name || ""}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.buyer_broker?.length > 0
                                        ? el.offer.buyer_broker.map((broker, index) => (
                                          <span key={broker.id || index}>
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.offer.buyer_broker.length - 1 && <br />}
                                          </span>
                                        ))
                                        : ""}
                                    </p>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.buyer_bds?.first_name} {el?.offer?.buyer_bds?.last_name || ""}
                                    </p>
                                  </div>

                                </div>

                                {/* --- GROSS COMMISSION SECTION --- */}
                                <p className="head-3 dark-H my-6 text-center">GROSS COMMISSION INCOME BREAKDOWN</p>

                                <div className="grid grid-cols-4 gap-6">
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_seller?.toLocaleString() || 0}
                                      </p>
                                      {el?.gcl_broker_seller_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.gcl_broker_seller_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_buyer?.toLocaleString() || 0}
                                      </p>
                                      {el?.gcl_broker_buyer_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.gcl_broker_buyer_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.gross_commission?.gci_to_broker_representing_seller
                                        ? `$${el.gross_commission?.gci_to_broker_representing_seller.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.gross_commission?.gci_to_broker_representing_buyer
                                        ? `$${el.gross_commission?.gci_to_broker_representing_buyer.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              {leadsData?.length > 0 && (
                <ContactPagination
                  activeTab={activeView}
                  paginationData={paginationData}
                  handleNext={handleNext}
                  handlePrev={handlePrev}
                  itemPerPage={itemPerPage}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <ExecutedAddModal showModal={showAddModal} onClose={() => setShowAddModal(false)} onCallApi={onSuccess} />
      <EditExecutedModal showModal={showEditModal} onClose={() => setShowEditModal(false)} id={selectedId} onCallApi={() => { setSelectedId(""); onSuccess(); }} />

      <ContractModal showModal={showContractModal} onClose={() => setShowContractModal(false)} id={selectedId} handleUnderContract={handleUnderContract} />

      <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />

      <FellOutContract from="executedloi" showModal={showfelloutcontract} id={selectedId} onClose={() => setShowFellOutContract(false)} onCallApi={onSuccess} />

      {showColumnModal && (
        <ColumnCustomizer
          allColumns={allColumns}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          storageKey="executedLoiVisibleColumns"
          onClose={() => setShowColumnModal(false)}
        />
      )}
    </BaseLayout>
  );
};

export default ExecutedLOI;